import classNames from "classnames";
import React, { CSSProperties, FunctionComponent } from "react";
import { fontTagToCssClass } from "../../../../agreed/fonts";
import { Content, ContentPage } from "../../../../types/customization/content";
import { Template, TemplateForcedTextDefinition } from "../../../../types/customization/template";
import { lengthMmToPx, sizeMmToPx } from "../../../../utils/customization/box";
import {
  findColorDefinition,
  findFontColorDefinition,
  findFontDefinition,
} from "../../../../utils/customization/finders";
import { findLocalizedContent } from "../../../../utils/customization/localized";
import {
  calcTextProperties,
  DEFAULT_FONT_COLOR,
  textAlignToCss,
  textOrientationToCss,
} from "../../../../utils/customization/text";
import "./style.scss";

interface Props {
  content: Content;
  elementDefinition: TemplateForcedTextDefinition;
  onClick?: () => void;
  page: ContentPage;
  ppmm: number;
  template: Template;
}

const ForcedText: FunctionComponent<Props> = ({
  content,
  elementDefinition,
  onClick,
  page,
  ppmm,
  template,
}) => {
  let fontKey = elementDefinition.fontKey;
  if (
    content.templateType === "book" &&
    elementDefinition.forcedType == "interpolated-content" &&
    elementDefinition.interpolatedContent === "title" &&
    content.titleFontKey
  ) {
    fontKey = content.titleFontKey;
  }

  const fontDefinition = findFontDefinition(fontKey, template);

  const { boxSize, fontSizeLength, lineHeightLength } = calcTextProperties(
    elementDefinition,
    fontDefinition
  );

  const style: CSSProperties = {
    ...textAlignToCss(elementDefinition.textAlign),
    ...textOrientationToCss(elementDefinition.textOrientation, sizeMmToPx(boxSize, ppmm)),
  };

  const colorDefinition = findColorDefinition(page.backgroundColorKey, template.colorDefinitions);
  const fontColorDefinition = colorDefinition
    ? findFontColorDefinition(elementDefinition.fontColorKey, colorDefinition)
    : undefined;

  const contentStyle: CSSProperties = {
    color: fontColorDefinition?.value || DEFAULT_FONT_COLOR,
    fontSize: `${lengthMmToPx(fontSizeLength, ppmm).value}px`,
    lineHeight: `${lengthMmToPx(lineHeightLength, ppmm).value}px`,
  };

  let forcedContent: string;
  let isPlaceholder = false;

  if (elementDefinition.forcedType === "forced-content") {
    const localizedContent = findLocalizedContent(elementDefinition.forcedContent, content.locale);
    forcedContent = localizedContent.content;
  } else if (elementDefinition.interpolatedContent) {
    if (
      (content.templateType === "book" || content.templateType === "calendar") &&
      elementDefinition.interpolatedContent === "title" &&
      !content.title &&
      elementDefinition.placeholder?.length
    ) {
      const localizedContent = findLocalizedContent(elementDefinition.placeholder, content.locale);
      forcedContent = localizedContent.content;
      isPlaceholder = true;
    } else if (
      (content.templateType === "book" || content.templateType === "calendar") &&
      elementDefinition.interpolatedContent === "title"
    ) {
      forcedContent = content.title || "";
    } else if (
      content.templateType === "calendar" &&
      elementDefinition.interpolatedContent === "date_title"
    ) {
      forcedContent = content.dateTitle || "";
    } else {
      throw new Error(
        `Unsupported forcedText interpolatedContent ${elementDefinition.interpolatedContent}`
      );
    }
  } else {
    throw new Error("Unsupported forcedText content");
  }

  return (
    <div className="ForcedText" onClick={onClick} style={style}>
      <div
        className={classNames("ForcedText-content", fontTagToCssClass(fontDefinition.fontTag), {
          "ForcedText-content-placeholder": isPlaceholder,
        })}
        style={contentStyle}
      >
        {forcedContent}
      </div>
    </div>
  );
};

export default ForcedText;
